import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/IntroAnimation.css';

const INTRO_MS = 4200;

const IntroAnimation = ({ onComplete }) => {
  const canvasRef = useRef(null);
  const endTimerRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [reducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const finish = useCallback(() => {
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    }
    if (onComplete && typeof onComplete === 'function') {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    if (reducedMotion) {
      finish();
      return;
    }

    setRendered(true);

    const letterElements = document.querySelectorAll('.intro-logo-letter');
    const subtitle = document.querySelector('.intro-subtitle');
    const eyebrowEn = document.querySelector('.intro-eyebrow-en');
    const eyebrowTr = document.querySelector('.intro-eyebrow-tr');

    letterElements.forEach((letter, index) => {
      letter.style.animationDelay = `${index * 0.12}s`;
    });
    if (eyebrowTr) {
      eyebrowTr.style.animationDelay = '0.1s';
    }
    if (eyebrowEn) {
      eyebrowEn.style.animationDelay = '0.35s';
    }
    if (subtitle) {
      subtitle.style.animationDelay = `${letterElements.length * 0.12 + 0.45}s`;
    }

    endTimerRef.current = setTimeout(finish, INTRO_MS);
    return () => {
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
    };
  }, [reducedMotion, finish]);

  useEffect(() => {
    if (!rendered || reducedMotion || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.2 + 0.3;
        this.speedX = (Math.random() - 0.5) * 0.35;
        this.speedY = (Math.random() - 0.5) * 0.35;
        const tones = [
          'rgba(198, 166, 100, 0.12)',
          'rgba(230, 210, 170, 0.1)',
          'rgba(160, 130, 75, 0.1)',
        ];
        this.color = tones[Math.floor(Math.random() * tones.length)];
        this.alpha = Math.random() * 0.5 + 0.15;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < -10 || this.x > canvas.width + 10 || this.y < -10 || this.y > canvas.height + 10) {
          this.reset();
        }
      }

      draw() {
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    for (let i = 0; i < 72; i++) {
      particles.push(new Particle());
    }

    let frame;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.update();
        p.draw();
      });
      frame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(frame);
    };
  }, [rendered, reducedMotion]);

  if (reducedMotion) {
    return null;
  }

  return (
    <div className="intro-animation" role="presentation">
      <div className="intro-vignette" aria-hidden />
      <div className="intro-grain" aria-hidden />
      <canvas ref={canvasRef} className="particle-background" aria-hidden />
      <div className="intro-content">
        <div className="intro-eyebrow-stack">
          <div className="intro-eyebrow intro-eyebrow-tr">Ankara · Lüks saat alım &amp; satış</div>
          <div className="intro-eyebrow-en">Luxury Watch Trading</div>
        </div>
        <div className="intro-logo" aria-label="KAIN Watches">
          <span className="intro-logo-letter">K</span>
          <span className="intro-logo-letter">A</span>
          <span className="intro-logo-letter">I</span>
          <span className="intro-logo-letter">N</span>
        </div>
        <div className="intro-subtitle">WATCHES</div>
      </div>
      <div className="intro-progress" aria-hidden>
        <div className="intro-progress-bar" style={{ animationDuration: `${INTRO_MS}ms` }} />
      </div>
    </div>
  );
};

export default IntroAnimation;
